import type { UrlParserData } from "./collection-filter/url-parser";
import type { GroupItemInfoT } from "./new-product-group-view/url-parser";

function* iterateParsedDatas<T>(
    value: null | undefined | T[] | Iterable<T> | T
) {
    if (value == null) {
        return;
    }
    const type = typeof value;
    if (type == "undefined") return;
    if (
        type == "bigint" ||
        type == "boolean" ||
        type == "function" ||
        type == "number" ||
        type == "string"
    ) {
        yield value;
        return;
    }
    if (type === "object") {
        if (type == null) return;
        const objVal = Object(value);
        if (Symbol.iterator in objVal && objVal[Symbol.iterator] != null) {
            for (const item of objVal) {
                yield item;
            }
            return;
        } else {
            yield value;
            return;
        }
    }
    throw new Error("Unsupported type: " + type);
}

export class EventRegistationObject<Type extends UrlParsersAndTypesKey> {
    /**
     * Collection of event registrations
     */
    public events: EventRegistrationItem[] = [];

    /**
     * @param name Name of event
     */
    getEvent(name: string): EventRegistrationItem {
        for (const eventRegistrationItem of this.events) {
            if (eventRegistrationItem.name === name)
                return eventRegistrationItem;
        }
        // Did not find event. Create new.
        const eventRegistrationItem = new EventRegistrationItem(name);
        this.events.push(eventRegistrationItem);
        return eventRegistrationItem;
    }

    register(registration: EventRegistration<Type>) {
        this.getEvent(registration.name).register(registration.handler);
    }

    trigger(name: string, ...args: any[]) {
        const event = this.getEvent(name);
        event.trigger(...args);
    }
}

type EventRegistration<Type extends UrlParsersAndTypesKey> =
    | {
          name: "applyAll";
          handler(
              eventObject: EventObject,
              parsedDatas: ParsedData<Type>[]
          ): void | Promise<void>;
      }
    | {
          name: "applyEmptySubPath";
          handler(): void;
      };

class EventRegistrationItem {
    /**
     * Name of event
     */
    public name: string;

    /**
     * Collection of event registrations
     */
    public handlers: { (eventObject: EventObject, ...args: any[]): any }[] = [];

    constructor(name: string) {
        this.name = name;
    }

    register(handler: { (eventObject: EventObject, ...args: any[]): any }) {
        this.handlers.push(handler);
    }

    async trigger(...args: any[]) {
        for (const handler of this.handlers) {
            const eventObject = new EventObject(this.name);
            const funcArgs: [eventObject: EventObject, ...args: any[]] = [
                eventObject,
            ];
            for (const arg of args) {
                funcArgs.push(arg);
            }
            let result = handler(...funcArgs);
            while (result instanceof Promise) {
                result = await result;
            }
            console.debug("handler", result);
            if (eventObject.cancel === true) break;
        }
    }
}

export class EventObject {
    /**
     * Name of event
     */
    public name: string;
    /**
     * Cancel all other events
     */
    public cancel: boolean = false;
    constructor(name: string) {
        this.name = name;
    }
}

function removeTrailingSlashes(str: string) {
    while (str.indexOf("/") === 0) {
        str = str.substr(1);
    }
    return str;
}

export class SplitUrlParser {
    /**
     * Path as string
     */
    public part: string;
    /**
     * Specifications
     */
    public specifications: SplitUrlParserSpecification[] = [];
    constructor(pathPart: string) {
        pathPart = removeTrailingSlashes(pathPart);
        this.part = pathPart;
    }

    addSpecification(partName: any, data: any) {
        const spec = new SplitUrlParserSpecification(partName, data);
        this.specifications.push(spec);
        this.sortSpecifications();
    }

    sortSpecifications() {
        this.specifications = this.specifications.sort(function (a, b) {
            return b.parts.length - a.parts.length;
        });
    }

    getSelectedSpecifications() {
        this.sortSpecifications();
        const result = [];
        const parts = [this.part.split("-")];
        for (const spec of this.specifications) {
            if (parts.length === 0) break;
            const match = spec.getMatchIndexes(parts);
            if (match === false) continue;
            match.removeFromSegmentsArray(parts);
            result.push(spec);
        }
        if (parts.length !== 0) {
            const missingParts =
                "(" + parts.map((x) => x.join(", ")).join(") and (") + ")";
            if (result.length == 0) {
                console.debug("did not parse any parts of: " + missingParts);
            } else {
                console.debug(
                    "found parts " +
                        result.map((x) => x.part).join(", ") +
                        " but was missing " +
                        missingParts
                );
            }
            return null;
        }
        return result;
    }
}

export class SplitUrlParserSpecification {
    /**
     * Part string
     */
    public part: string;
    /**
     * Data
     */
    public data: any;
    /**
     * Parts splitted to array
     */
    public parts: string[];

    constructor(part: string, data: any) {
        part = removeTrailingSlashes(part);

        this.part = part;
        this.data = data;
        this.parts = part.split("-");
    }

    getMatchIndexes(segments: string[][]) {
        for (const segment of segments) {
            const result = this.getMatchIndexesOnSegment(segment);
            if (result === false) continue;
            return new SplitUrlParserSpecIndexResult(
                segments.indexOf(segment),
                result
            );
        }
        return false;
    }

    /**
     * @param parts Array with splitted chunks of part string
     */
    getMatchIndexesOnSegment(parts: string[]) {
        let startIndex = 0;
        while (true) {
            const index = parts.indexOf(this.parts[0], startIndex);
            let match = true;
            if (index === -1) break;
            startIndex = index + 1;

            const indexes = [];
            indexes.push(index);

            for (let i = 1; i < this.parts.length; i++) {
                const expectedIndex = index + i;
                if (
                    parts.indexOf(this.parts[i], expectedIndex) !==
                    expectedIndex
                ) {
                    match = false;
                    break;
                }
                indexes.push(expectedIndex);
            }

            if (indexes.length !== this.parts.length) {
                match = false;
            }

            if (match) {
                return new SplitUrlParserSpecSegmentResult(
                    indexes[0],
                    indexes.length
                );
            }
        }
        return false;
    }
}

class SplitUrlParserSpecIndexResult {
    /**
     * Index of segment
     */
    public index: number;
    /**
     * Segment result
     */
    public segmentResult: SplitUrlParserSpecSegmentResult;

    constructor(index: any, segmentResult: SplitUrlParserSpecSegmentResult) {
        this.index = index;
        this.segmentResult = segmentResult;
    }

    removeFromSegmentsArray(segments: string[][]) {
        const segment = segments[this.index];
        if (segment == null)
            throw new Error("Did not find segment in collection");
        this.segmentResult.removeFromSegment(segment);
        if (segment.length === 0) {
            segments.splice(segments.indexOf(segment), 1);
        }
    }
}

class SplitUrlParserSpecSegmentResult {
    /**
     * Start index
     */
    public index: number;
    /**
     * Length of matched indexes
     */
    public length: number;
    constructor(index: number, length: number) {
        this.index = index;
        this.length = length;
    }

    removeFromSegment(segment: string[]) {
        const removed = segment.splice(this.index, this.length);
        if (removed.length !== this.length) {
            throw new Error("Did not remove expected count of items!");
        }
    }
}

export interface ParseFunction<
    Type extends UrlParsersAndTypesKey,
    TItem extends UrlParsersAndTypes[Type]["item"] = UrlParsersAndTypes[Type]["item"]
> {
    (args: ParserArgsTyped<Type>): TItem | TItem[] | Generator<TItem> | null;
}

export class UrlParser<Type extends UrlParsersAndTypesKey> {
    /**
     * Name / type of parser
     */
    name: Type;
    parseFunction: ParseFunction<UrlParsersAndTypesKey>;
    eventController: EventRegistationObject<Type>;

    constructor(
        name: Type,
        parseFunction: ParseFunction<Type>,
        registration:
            | { (registration: EventRegistationObject<Type>): any }
            | undefined = undefined
    ) {
        this.name = name;
        this.parseFunction =
            parseFunction as ParseFunction<UrlParsersAndTypesKey>;
        this.eventController = new EventRegistationObject<Type>();
        if (typeof registration === "function")
            registration(this.eventController);
    }

    parse(
        part: string,
        parsedDatas: ParsedData<Type>[],
        matchedLogicalName: string,
        matchedLogicalNameModuleGuid: number | undefined,
        matchedLogicalNameSubModuleGuid: number | undefined
    ): UrlResult<Type> | null {
        const output = this.parseFunction({
            part,
            parsedDatas,
            matchedLogicalName,
            matchedLogicalNameModuleGuid,
            matchedLogicalNameSubModuleGuid,
            matchedLogicalNameEntityGuid: undefined,
        });
        if (output === null || output === undefined) {
            return null;
        }

        return UrlResult.success<Type>(this.name as Type, output);
    }

    async applyAll(
        parsedDatas: ParsedData<Type>[],
        allParsedDatas: ParsedData<Type>[]
    ) {
        await this.eventController.trigger(
            "applyAll",
            parsedDatas,
            allParsedDatas
        );
    }

    apply(
        parsedData: ParsedData<Type>,
        parsedDatas: ParsedData<Type>[],
        allParsedDatas: ParsedData<Type>[] | ParsedData<Type>
    ) {
        this.eventController.trigger("apply", parsedDatas, allParsedDatas);
    }

    applyEmptySubPath() {
        this.eventController.trigger("applyEmptySubPath");
    }
}

class UrlResult<
    Type extends UrlParsersAndTypesKey,
    T extends UrlParsersAndTypes[Type]["item"] = UrlParsersAndTypes[Type]["item"]
> {
    /**
     * True if success, otherwise false
     */
    public success: boolean;
    /**
     * Type of parsed value (not used if non-success)
     */
    public type: Type | undefined | null;
    /**
     * Data extracted by the parser
     */
    public data: T | T[] | Generator<T> | null;

    constructor(
        success: boolean,
        type: Type | undefined | null,
        data: T | T[] | Generator<T> | null
    ) {
        this.success = success;
        this.type = type;
        this.data = data;
    }

    static success<
        Type extends UrlParsersAndTypesKey,
        T extends UrlParsersAndTypes[Type]["item"] = UrlParsersAndTypes[Type]["item"]
    >(type: Type, data: T | T[] | Generator<T> | null) {
        return new UrlResult<Type>(true, type, data);
    }
}

export class ParsedData<
    TType extends UrlParsersAndTypesKey,
    TData extends UrlParsersAndTypes[TType]["item"] = UrlParsersAndTypes[TType]["item"]
> {
    /**
     * Part name
     */
    public part: string;
    /**
     * Parser type/name
     */
    public type: TType;
    /**
     * Data
     */
    public data: TData;
    /**
     * Parser instance
     */
    public parser: UrlParser<TType>;
    constructor(
        { part }: { part: string },
        type: TType,
        parser: UrlParser<TType>,
        data: TData
    ) {
        this.part = part;
        this.type = type;
        this.data = data;
        this.parser = parser;
    }
}

export type UrlParserKeys_Group = "product-group";
export type UrlParserKeys_ProductCollection = "product-collection";
export type UrlParserKeys_RegionSet = "region-set";
export type UrlParserKeys =
    | UrlParserKeys_Group
    | UrlParserKeys_ProductCollection
    | UrlParserKeys_RegionSet;

type UrlParserType_ProductCollection = {
    key: UrlParserKeys_ProductCollection;
    item: UrlParserData;
};

type UrlParserType_RegionSet = {
    key: UrlParserKeys_RegionSet;
    item: SplitUrlParserSpecification;
};

type UrlParserType_Group = {
    key: UrlParserKeys_Group;
    item: GroupItemInfoT;
};

type UrlParserType<Events extends { key: UrlParserKeys }> = {
    [E in Events as E["key"]]: E;
};

type UrlParsersAndTypes = UrlParserType<
    | UrlParserType_RegionSet
    | UrlParserType_ProductCollection
    | UrlParserType_Group
>;

export type UrlParsersAndTypesKey = keyof UrlParsersAndTypes;

class ParsedDataGroup<Type extends UrlParsersAndTypesKey> {
    /**
     * Type name
     */
    type: Type;
    /**
     * Array of parsed datas of type
     */
    parsedDatas: ParsedData<Type>[] = [];

    constructor(type: Type) {
        this.type = type;
    }
}

interface ParserArgs {
    part: string;
    parsedDatas: ParsedData<UrlParsersAndTypesKey>[];
    matchedLogicalName: string;
    matchedLogicalNameModuleGuid: number | undefined;
    matchedLogicalNameSubModuleGuid: number | undefined;
    matchedLogicalNameEntityGuid: number | undefined;
}

export interface ParserArgsTyped<T extends UrlParsersAndTypesKey> {
    part: string;
    parsedDatas: ParsedData<T>[];
    matchedLogicalName: string;
    matchedLogicalNameModuleGuid: number | undefined;
    matchedLogicalNameSubModuleGuid: number | undefined;
    matchedLogicalNameEntityGuid: number | undefined;
}

export class UrlResolver {
    /**
     * Array of parsers
     */
    public parsers: UrlParser<UrlParsersAndTypesKey>[] = [];

    matchedLogicalName: string | undefined;
    matchedLogicalNameModuleGuid: number | undefined;
    matchedLogicalNameSubModuleGuid: number | undefined;
    matchedLogicalNameEntityGuid: number | undefined;

    setMatchedLogicalData(
        matchedLogicalName: string,
        matchedLogicalNameModuleGuid: number,
        matchedLogicalNameSubModuleGuid: number,
        matchedLogicalNameEntityGuid: number
    ) {
        this.matchedLogicalName = matchedLogicalName;
        this.matchedLogicalNameModuleGuid = matchedLogicalNameModuleGuid;
        this.matchedLogicalNameSubModuleGuid = matchedLogicalNameSubModuleGuid;
        this.matchedLogicalNameEntityGuid = matchedLogicalNameEntityGuid;
    }

    async parseCurrentPathName() {
        const parsedData = await this.resolveCurrentPathName();
        return await this.applyResolvedUrl(parsedData);
    }

    async parsePathName(pathname: string) {
        const parsedData = await this.resolvePathName(pathname);
        return await this.applyResolvedUrl(parsedData);
    }

    async resolveCurrentPathName() {
        return await this.resolvePathName(location.pathname);
    }

    async resolvePathName(pathname: string) {
        return await this.resolveUrl(
            pathname,
            this.matchedLogicalName ?? "/",
            this.matchedLogicalNameModuleGuid,
            this.matchedLogicalNameSubModuleGuid,
            this.matchedLogicalNameEntityGuid
        );
    }

    groupByType(
        allParsedData: ParsedData<UrlParsersAndTypesKey>[]
    ): ParsedDataGroup<UrlParsersAndTypesKey>[] {
        const getEntry = function (
            agg: ParsedDataGroup<UrlParsersAndTypesKey>[],
            val: ParsedData<UrlParsersAndTypesKey>
        ) {
            return agg.filter((x) => x.type === val.type)[0];
        };
        return allParsedData.reduce(function (
            agg: ParsedDataGroup<UrlParsersAndTypesKey>[],
            val
        ) {
            let entry = getEntry(agg, val);
            if (entry == null) {
                agg.push(new ParsedDataGroup(val.type));
            }
            entry = getEntry(agg, val);
            if (entry == null) throw new Error("failed to add entry");
            entry.parsedDatas.push(val);
            return agg;
        },
        []);
    }

    /**
     * Resolve the given url by the loaded components
     * @param currentUrl A path name starting with "/" and then the part (without query string)
     * @param matchedLogicalName The first part of the logicalName which is recognised and handled by a controller action
     */
    async resolveUrl(
        currentUrl: string,
        matchedLogicalName: string,
        matchedLogicalNameModuleGuid: number | undefined = undefined,
        matchedLogicalNameSubModuleGuid: number | undefined = undefined,
        matchedLogicalNameEntityGuid: number | undefined = undefined
    ) {
        currentUrl = currentUrl.toLowerCase();
        console.warn("LOOKING FOR " + currentUrl);
        if (currentUrl.indexOf(matchedLogicalName.toLowerCase()) !== 0)
            throw new Error(
                "Could not resolve group path of url: " + currentUrl
            );
        let subPath = currentUrl.substring(matchedLogicalName.length);

        const parsedDatas: ParsedData<UrlParsersAndTypesKey>[] = [];

        let hasRunOnce = false;

        do {
            if (hasRunOnce && subPath.indexOf("/") !== 0)
                throw new Error("could not parse url. missing /");

            let part = subPath;
            if (hasRunOnce === false && subPath.length === 0) {
                part = "";
            } else if (part.indexOf("/", 1) !== -1) {
                part = part.substring(0, part.indexOf("/", 1));
            }

            const match = this.parsePart({
                part,
                parsedDatas,
                matchedLogicalName,
                matchedLogicalNameModuleGuid,
                matchedLogicalNameSubModuleGuid,
                matchedLogicalNameEntityGuid,
            });

            for (const matchItem of match) parsedDatas.push(matchItem);

            subPath = subPath.substring(part.length);
            hasRunOnce = true;
        } while (subPath.length > 0);

        console.debug("parsedDatas", parsedDatas);

        return parsedDatas;
    }

    async applyResolvedUrl(parsedDatas: ParsedData<UrlParsersAndTypesKey>[]) {
        // this.postFormChange(group, true);
        //console.log("TODO Whats next?");
        const byType = this.groupByType(parsedDatas);

        for (const typeEntry of byType) {
            const parser = typeEntry.parsedDatas[0].parser;
            for (const parsedData of typeEntry.parsedDatas) {
                parser.apply(parsedData, typeEntry.parsedDatas, parsedData);
            }
            await parser.applyAll(typeEntry.parsedDatas, parsedDatas);
        }

        if (parsedDatas.length === 0) {
            this.applyEmptySubPath();
        }

        return parsedDatas;
    }

    addParser(parser: UrlParser<UrlParsersAndTypesKey>) {
        this.parsers.push(parser);
    }

    *parsePart(
        parserArgs: ParserArgs
    ): Generator<ParsedData<UrlParsersAndTypesKey>, void, unknown> {
        for (const parser of this.parsers) {
            const result = parser.parse(
                parserArgs.part,
                parserArgs.parsedDatas.filter((x) => x.type == parser.name),
                parserArgs.matchedLogicalName,
                parserArgs.matchedLogicalNameModuleGuid,
                parserArgs.matchedLogicalNameSubModuleGuid
            );

            if (result == null || result.success === false) {
                continue;
            }

            const resultItems = Array.from(iterateParsedDatas(result.data));

            for (const resultItem of resultItems) {
                if (result.type == null) continue;
                yield new ParsedData(
                    parserArgs,
                    result.type,
                    parser,
                    resultItem
                );
            }
            return;
        }
    }

    applyEmptySubPath() {
        for (const parser of this.parsers) {
            parser.applyEmptySubPath();
        }
    }
}
